import React, { Suspense, useEffect, useState } from 'react';
import { Scoped } from 'kremling';
import { createBrowserHistory } from 'history';
import { Router, Route, Switch } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import styles from './root.styles.scss';
const AuthRoutes = React.lazy(() => import(/* webpackChunkName: "auth" */'./pages/auth/auth-routes.component').then(m => ({
  default: m.AuthRoutes
})));
const SidebarMui = React.lazy(() => import(/* webpackChunkName: "app" */'components/sidebar/sidebarMui.component').then(m => ({
  default: m.SidebarMui
})));
const AuthCheck = React.lazy(() => import(/* webpackChunkName: "app" */'./components/auth-check.component').then(m => ({
  default: m.AuthCheck
})));
import { Toaster } from './components/toaster/toaster.component';
import { SnackbarToaster } from './components/mui/snackbar/snackbar.component';
const AppRoutes = React.lazy(() => import(/* webpackChunkName: "app" */'./app-routes').then(m => ({
  default: m.AppRoutes
})));
const Unsubscribe = React.lazy(() => import(/* webpackChunkName: "unsubscribe" */'./pages/unsubscribe.component').then(m => ({
  default: m.Unsubscribe
})));
const SmsSignUp = React.lazy(() => import(/* webpackChunkName: "sign-up" */'./pages/sms-sign-up.component').then(m => ({
  default: m.SmsSignUp
})));
const CustomerDrawerComponent = React.lazy(() => import(/* webpackChunkName: "customer-drawer" */'./pages/customer-drawer/customer-drawer.component').then(m => ({
  default: m.CustomerDrawerComponent
})));
import { ErrorBoundary } from './components/error-boundary/error-boundary.component';
import { Loader } from './components/loader/loader.component';
import { getStatus } from './shared/common.api';
import userflow from './userflow';
const AddressRoutes = React.lazy(() => import(/* webpackChunkName: "address" */'./pages/domains/address-routes.component').then(m => ({
  default: m.AddressRoutes
})));
import { ThemeProvider } from '@mui/material/styles';
import theme from './shared/theme';
import { api } from './shared/api';
import { ContextProviders } from './context/context-providers';
import { AppNotices } from './components/app-notices.component';
import CssBaseline from '@mui/material/CssBaseline';
import { snackbarService } from './components/mui/snackbar/snackbar-service';
const SidebarRight = React.lazy(() => import(/* webpackChunkName: "app" */'components/sidebar/sidebarRight.component').then(m => ({
  default: m.SidebarRight
})));
export const history = createBrowserHistory();
export const Root = () => {
  const [loadSidebar, setLoadSidebar] = useState(true);
  useEffect(() => {
    // Keep in-flow "Go to page" steps on the client router instead of a full
    // page load, which would drop the flow mid-way.
    userflow.setCustomNavigate(url => history.push(url));
  }, []);
  useEffect(() => {
    // On all API requests check if there is a network error.
    let retryCounter = 0;
    api.interceptors.response.use(res => {
      retryCounter = 0;
      return res;
    }, err => {
      if (err.message === 'Network Error') {
        if (retryCounter < 3) {
          const config = err.config;
          retryCounter += 1;
          // TODO add a time delay?
          return new Promise(resolve => {
            resolve(api(config));
          });
        } else {
          // Snackbar here
          snackbarService.popup({
            type: 'warning',
            message: 'Currently experiencing network connectivity issues.'
          });
        }
      }
      return Promise.reject(err);
    });

    // Ping the status endpoint once to check if we're online
    getStatus();

    // TODO move sidebar stuff to sub route component since it doesn't affect all of these routes.
    // sidebar query param to hide the side bar
    const sidebarParam = new URLSearchParams(window.location.search).get('sidebar');
    if (sidebarParam == 'false') {
      setLoadSidebar(false);
    }
  }, []);
  return <>
      <ThemeProvider theme={theme}>
        <Router history={history}>
          <ContextProviders>
            <Scoped css={styles}>
              <div>
                <ErrorBoundary>
                  <>
                    <CssBaseline />
                    <div className="root">
                      <Switch>
                        {/* auth pages + support + no-company (lazy "auth" bundle) */}
                        <Route path={['/sign-in', '/mfa', '/auth', '/support', '/no-company']} render={props => <Suspense fallback={<Loader overlay />}>
                              <AuthRoutes {...props} />
                            </Suspense>} />

                        {/*unsubscribe page*/}
                        <Route path="/unsubscribe/:id" exact render={props => <Suspense fallback={<Loader overlay />}>
                              <Unsubscribe {...props} />
                            </Suspense>} />

                        {/*SMS sign-up page for 10DLC (lazy "sign-up" bundle)*/}
                        <Route path="/sign-up/:uuid" exact render={props => <Suspense fallback={<Loader overlay />}>
                              <SmsSignUp {...props} />
                            </Suspense>} />

                        {/*address verify/reject pages (lazy "address" bundle)*/}
                        <Route path="/address" render={props => <Suspense fallback={<Loader overlay />}>
                              <AddressRoutes {...props} />
                            </Suspense>} />

                        {/* Customer Drawer Component Route (lazy "customer-drawer" bundle) */}
                        <Route path="/customer-drawer" exact render={props => <Suspense fallback={<Loader overlay />}>
                              <CustomerDrawerComponent {...props} />
                            </Suspense>} />

                        <Route path="/" render={props => <Suspense fallback={<Loader overlay />}>
                              <AuthCheck {...props}>
                                <AppNotices />
                                {loadSidebar && <SidebarMui {...props} />}
                                <AppRoutes {...props} />
                                <SidebarRight {...props} />
                              </AuthCheck>
                            </Suspense>} />
                      </Switch>
                    </div>
                  </>
                </ErrorBoundary>
              </div>
              <Toaster />
              <SnackbarToaster />
            </Scoped>
          </ContextProviders>
        </Router>
      </ThemeProvider>
    </>;
};
export default Sentry.withProfiler(Root);